// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useAfterPaint from "@store/Framework/Common/Hooks/useAfterPaint";
import Icon, { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import ProductSelector, {
    ProductSelectorPresentationProps,
    ProductSelectorProps,
} from "@store/Overrides/Components/ProductSelector";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { HTMLAttributes, useState } from "react";
import { css } from "styled-components";

export interface QuickAddProductPresentationProps extends ExpandStyles<QuickAddProductStyles> {
    /** Optional leading icon rendered before the title (e.g. CartQuickAdd's power bolt). */
    iconSrc?: IconPresentationProps["src"];
    /** Whether the accordion starts expanded. */
    initialExpanded?: boolean;
}

export interface QuickAddProductProps
    extends QuickAddProductPresentationProps,
        Omit<HTMLAttributes<HTMLElement>, "title"> {
    /** Main accordion header label. */
    title: React.ReactNode;
    /** Optional secondary header text rendered next to the title. */
    subtitle?: React.ReactNode;
    /** Props forwarded to the inner ProductSelector (styling is owned via the `productSelector` style key). */
    productSelectorProps: Omit<ProductSelectorProps, keyof ProductSelectorPresentationProps>;
}

export interface QuickAddProductStyles {
    wrapper?: InjectableCss;
    accordion?: AccordionSectionPresentationProps;
    accordionTitleIcon?: IconPresentationProps;
    accordionTitleWrapper?: InjectableCss;
    accordionTitle?: TypographyPresentationProps;
    accordionSubtitle?: TypographyPresentationProps;
    productSelector?: ProductSelectorPresentationProps;
}

export const quickAddProductStyles: QuickAddProductStyles = {
    accordion: {
        mergeCss: true,
        headerProps: {
            css: css`
                button[aria-expanded="true"] {
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                }
            `,
        },
        panelProps: {
            css: css`
                border-color: ${getColor("primary")};
            `,
        },
    },
    accordionTitleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(2)};
        `,
    },
    accordionTitleIcon: {
        size: 24,
        color: "inherit",
    },
    accordionSubtitle: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.base};
            font-weight: ${x => x.theme.design.typography.weight.base};
            margin-left: ${pxToRem(4)};
        `,
    },
};

const QuickAddProduct = ({
    title,
    subtitle,
    iconSrc,
    initialExpanded,
    productSelectorProps,
    extendedStyles,
    overridedStyles,
    ...htmlAttributes
}: QuickAddProductProps) => {
    const styles = useExpandProps(quickAddProductStyles, extendedStyles, overridedStyles);
    // InlineFormButton measures layout on render; bump this key after panel paint so spacing recalculates on expand.
    const [expandKey, setExpandKey] = useState(0);
    const scheduleAfterPaint = useAfterPaint();

    const accordionTitle = (
        <StyledWrapper {...styles.accordionTitleWrapper}>
            {iconSrc ? <Icon {...styles.accordionTitleIcon} src={iconSrc} /> : null}
            <span>
                <Typography {...styles.accordionTitle}>{title}</Typography>
                {subtitle ? <Typography {...styles.accordionSubtitle}>{subtitle}</Typography> : null}
            </span>
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.wrapper} {...htmlAttributes}>
            <ManagedAccordionSection
                {...styles.accordion}
                title={accordionTitle}
                initialExpanded={initialExpanded}
                onTogglePanel={expanded => {
                    if (expanded) {
                        scheduleAfterPaint(() => setExpandKey(k => k + 1));
                    }
                }}
            >
                <ProductSelector
                    {...styles.productSelector}
                    {...productSelectorProps}
                    additionalButtonUpdateProps={[
                        expandKey,
                        ...(productSelectorProps.additionalButtonUpdateProps || []),
                    ]}
                />
            </ManagedAccordionSection>
        </StyledWrapper>
    );
};

export default QuickAddProduct;
